/* eslint-disable jsx-a11y/no-autofocus -- the password is the only field */
import React, { useState } from 'react';
import { Text } from 'rebass';
import Button from '@/components/Button/Button';
import { Input, Label } from '@/components/Form';
import Form from '@/components/Form/Form';
import FormColumn from '@/components/Form/FormColumn';
import FormRow from '@/components/Form/FormRow';
import Modal, { ModalBody, ModalFooter } from '@/components/Modal';
import ErrorText from '@/components/Form/ErrorText';

type ConfirmPasswordModalProps = {
  title: string;
  confirmLabel: string;
  confirmVariant?: 'danger' | 'primary';
  description: string;
  onConfirm: (password: string) => Promise<unknown>;
  onClose: () => void;
};

const ConfirmPasswordModal: React.FC<ConfirmPasswordModalProps> = ({
  title,
  confirmLabel,
  confirmVariant = 'danger',
  description,
  onConfirm,
  onClose,
}) => {
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const canConfirm = !loading && !!password;

  const confirm = async () => {
    if (!canConfirm) return;
    setLoading(true);
    setErrorMessage('');
    try {
      await onConfirm(password);
    } catch (err) {
      setErrorMessage((err as Error).message);
      setLoading(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <ModalBody>
        <Form onSubmit={confirm}>
          <Text variant="body2" color="fieldBorderHover">
            {description} Enter your current password to confirm.
          </Text>
          <FormRow>
            <FormColumn>
              <Label htmlFor="confirm-current-password">Current password</Label>
              <Input
                id="confirm-current-password"
                type="password"
                autoFocus
                autoComplete="current-password"
                width="100%"
                value={password}
                disabled={loading}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setErrorMessage('');
                }}
              />
            </FormColumn>
          </FormRow>
          <ErrorText message={errorMessage} />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant={confirmVariant}
          onClick={confirm}
          disabled={!canConfirm}
        >
          {confirmLabel}
        </Button>
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmPasswordModal;
